import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";

import styles from "./liveScoresStyles";
import LiveScoresView from "./liveScoresView";
import ApiTour from "../../../API/ApiTour";
import ApiGameAnalytics from "../../../API/ApiGameAnalytics";
import {
  attachNewSessions,
  clearRun,
  createRun,
  loadRun,
  saveRun,
} from "./liveRunStorage";

// Rythme plus soutenu que la page sessions : cet écran est fait pour être
// regardé pendant qu'un groupe joue.
export const LIVE_POLL_INTERVAL_MS = 15000;

// Fenêtre interrogée à chaque tour. Large volontairement : un groupe complet
// démarre en quelques minutes, et l'enregistrement ne doit rater personne.
const PAGE_SIZE = 100;

class LiveScoresComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tourId: null,
      tour: null,
      run: null,
      sessions: [],
      loading: true,
      refreshing: false,
      lastRefreshedAt: null,
      errorMessage: "",
    };
    this.pollTimer = null;
  }

  componentDidMount() {
    document.title = "Suivi en direct | Landing Zone";

    const tourId = parseInt(
      new URLSearchParams(window.location.search).get("tourId"),
      10
    );

    if (!tourId || Number.isNaN(tourId)) {
      this.setState({ loading: false, errorMessage: "ID du parcours manquant" });
      return;
    }

    this.setState({ tourId, run: loadRun(tourId) }, () => {
      this.getTour();
      this.fetchSessions();
      this.startPolling();
    });

    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  componentWillUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  }

  startPolling = () => {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      this.fetchSessions();
    }, LIVE_POLL_INTERVAL_MS);
  };

  handleVisibilityChange = () => {
    if (document.visibilityState === "visible" && this.state.tourId) {
      this.fetchSessions();
    }
  };

  // `/tourPublic` et non `/tour` : cette page est consultable sans compte, elle
  // ne doit appeler que des routes publiques (sinon 401 → redirection /login).
  getTour = async () => {
    const response = await ApiTour.getTourPublic(this.state.tourId);
    if (response.status === 200) {
      this.setState({ tour: response.data });
    }
  };

  fetchSessions = async () => {
    const { tourId, run } = this.state;
    if (!tourId) return;

    // Sans enregistrement ouvert, l'écran n'affiche aucune session : interroger
    // l'API ne servirait qu'à charger le back pour un résultat jeté.
    if (!run) {
      this.setState({ loading: false, refreshing: false });
      return;
    }

    this.setState({ refreshing: true });

    // La fenêtre de l'enregistrement est envoyée au back, qui ne renvoie que
    // les sessions concernées au lieu de l'historique complet du parcours.
    const response = await ApiGameAnalytics.getPublicSessions(
      tourId,
      1,
      PAGE_SIZE,
      "created_at",
      run.startedAt,
      run.closedAt
    );

    if (response.status !== 200 || !response.data?.items) {
      // Un échec ponctuel ne doit pas vider l'écran affiché au public.
      this.setState({ refreshing: false, loading: false });
      return;
    }

    const sessions = response.data.items;

    this.setState((prevState) => {
      const updatedRun = attachNewSessions(prevState.run, sessions);
      if (updatedRun) saveRun(updatedRun);

      return {
        sessions,
        run: updatedRun || prevState.run,
        loading: false,
        refreshing: false,
        lastRefreshedAt: new Date(),
        errorMessage: "",
      };
    });
  };

  // Ouvre un enregistrement : à partir de maintenant, toute session qui démarre
  // sur ce parcours rejoint le tableau.
  handleStartRun = () => {
    const run = createRun(this.state.tourId);
    saveRun(run);
    this.setState({ run }, this.fetchSessions);
  };

  // Ferme l'enregistrement sans effacer le tableau : les parties en cours
  // continuent d'être suivies jusqu'à leur fin, mais aucune nouvelle session
  // n'est rattachée.
  handleCloseRun = () => {
    const { run } = this.state;
    if (!run || run.closedAt) return;

    const closedRun = { ...run, closedAt: new Date().toISOString() };
    saveRun(closedRun);
    this.setState({ run: closedRun });
  };

  handleDiscardRun = () => {
    clearRun(this.state.tourId);
    this.setState({ run: null });
  };

  render() {
    return (
      <LiveScoresView
        {...this.props}
        state={this.state}
        classes={this.props.classes}
        pollIntervalMs={LIVE_POLL_INTERVAL_MS}
        onStartRun={this.handleStartRun}
        onCloseRun={this.handleCloseRun}
        onDiscardRun={this.handleDiscardRun}
        onRefreshNow={this.fetchSessions}
      />
    );
  }
}

export default withStyles(styles, { withTheme: true })(
  withRouter(LiveScoresComponent)
);
